import { UseQueryResult } from "@tanstack/react-query";
import { unwrapConnection } from "@utils/utils";
import { endOfDay, isAfter, isBefore, parseISO, startOfDay, subDays, subMonths } from "date-fns";
import React, { PropsWithChildren, useMemo, useReducer, useState } from "react";

import {
  GetReportByIdQuery,
  ReportGoalType,
  useGetReassessmentReportGoalsQuery,
  useTreatmentPlanTemplateQuery,
} from "@api/graphql/types-and-hooks";
import {
  EMPTY_FILTER_VALUES,
  GoalsFilterValues,
} from "@pages/ClinicalReportDetails/ReportPage/Components/GoalsFilterPopover";
import { useClinicalReportDetailsData } from "@providers/ClinicalReportDetailsProvider";
import { ReportProgrammingContext, selectionReducer } from "@providers/ReportProgrammingContext";

interface ReportProgrammingProviderProps extends PropsWithChildren {
  reportQuery: UseQueryResult<GetReportByIdQuery, unknown>;
}
export const ReportProgrammingProvider: React.FC<ReportProgrammingProviderProps> = ({ children, reportQuery }) => {
  const report = reportQuery?.data?.getReports?.edges?.[0]?.node;
  const { isProgressSummaryGenerating } = useClinicalReportDetailsData();
  const [selection, dispatch] = useReducer(selectionReducer, { mode: "none" });
  const [filters, setFilters] = useState<GoalsFilterValues>(EMPTY_FILTER_VALUES);

  const reassessmentGoalsQuery = useGetReassessmentReportGoalsQuery(
    {
      filter: {
        reportId: report?.id || "",
        ...(filters.status.length > 0 ? { status: filters.status } : {}),
        ...(filters.reviewed.length > 0 ? { reviewed: filters.reviewed } : {}),
      },
    },
    {
      queryKey: [
        "get-reassessment-goals",
        report?.id,
        filters.status,
        filters.reviewed,
        filters.dateRange,
        isProgressSummaryGenerating,
      ],
      enabled: !!report?.id,
    },
  );

  const allGoals: ReportGoalType[] = useMemo(
    () => (unwrapConnection(reassessmentGoalsQuery.data?.getReassessmentReportGoals) as ReportGoalType[]) || [],
    [reassessmentGoalsQuery.data],
  );
  const reassessmentGoalList: ReportGoalType[] = useMemo(() => {
    const { preset, startDate, endDate } = filters.dateRange;
    if (!preset) return allGoals;

    const now = new Date();
    let rangeStart: Date | null = null;
    let rangeEnd: Date | null = null;

    if (preset === "last30") rangeStart = subDays(now, 30);
    else if (preset === "last60") rangeStart = subDays(now, 60);
    else if (preset === "last90") rangeStart = subDays(now, 90);
    else if (preset === "last6months") rangeStart = subMonths(now, 6);
    else if (preset === "last12months") rangeStart = subMonths(now, 12);
    else if (preset === "custom") {
      rangeStart = startDate ? startOfDay(parseISO(startDate)) : null;
      rangeEnd = endDate ? endOfDay(parseISO(endDate)) : null;
    }

    return allGoals.filter((goal) => {
      if (!goal.createdDate) return true;
      const goalDate = parseISO(goal.createdDate as string);
      if (rangeStart && isBefore(goalDate, rangeStart)) return false;
      if (rangeEnd && isAfter(goalDate, rangeEnd)) return false;
      return true;
    });
  }, [allGoals, filters.dateRange]);

  // Fetch treatment plan template to get GAS configuration
  const treatmentPlanTemplateQuery = useTreatmentPlanTemplateQuery(
    {
      reportId: report?.id || "",
    },
    {
      enabled: !!report?.id,
      retry: false,
    },
  );
  const treatmentPlanTemplate = treatmentPlanTemplateQuery?.data?.treatmentPlanTemplate;
  const isGasEnabled = treatmentPlanTemplate?.isGasEnabled || false;

  return (
    <ReportProgrammingContext.Provider
      value={{
        reassessmentGoalsQuery,
        selected: selection,
        dispatchSelected: dispatch,
        isGasEnabled,
        filters,
        setFilters,
        reassessmentGoalList,
        allGoals,
      }}
    >
      {children}
    </ReportProgrammingContext.Provider>
  );
};

// Re-export context types and hook for convenience
export { useReportProgrammingData } from "@providers/ReportProgrammingContext";
